import * as React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import IconButton from '@mui/material/IconButton';
import SearchIcon from '@mui/icons-material/Search';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import Chip from '@mui/material/Chip';
import { useTheme } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { getClients } from '../../../store/slices/clientsSlice';
import { getUser } from '../../../store/slices/userSlice';
import { getClientChipStyles } from '../../../utils/clientColors';
import ClientDetailsDialog from './ClientDetailsDialog';
import AddClientDialog from './AddClientDialog';
import { canAddOrEditClient, isOwner, isGeneralManager } from '../../../utils/permissions';
import useIsMobile from '../../../hooks/useIsMobile';
import { scaleColumnsForMobile } from '../../../utils/mobileDataGrid';
import {
  GRID_PREFERENCES_STORAGE_KEY,
  DEFAULT_GRID_PREFERENCE,
  parseGridPreferences,
  sanitizeGridPreference,
  applyColumnOrder,
  ClientsColumnsPopover,
} from './ClientsColumnsHelper';

const paginationModel = { page: 0, pageSize: 30 };

export default function Clients() {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();
  const clients = useSelector(getClients);
  const user = useSelector(getUser);
  const [selectedClient, setSelectedClient] = React.useState(null);
  const [addClientDialogOpen, setAddClientDialogOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState('');
  const [columnsPanelAnchorEl, setColumnsPanelAnchorEl] = React.useState(null);
  const [gridPreferences, setGridPreferences] = React.useState(() => parseGridPreferences());

  // !!selectedClient in deps: re-runs when dialog opens/closes so chips reflect any color change the user just made
  const columns = React.useMemo(() => [
    // {field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'client_name',
      headerName: 'Client Name',
      width: 200,
      renderCell: (params) => {
        const clientId = params.row.id;
        const label = params.value || '';
        const { bgColor, textColor, borderColor } = getClientChipStyles(clientId);
        return (
          <Chip
            label={label}
            sx={{
              backgroundColor: bgColor,
              color: textColor,
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '6px',
              border: `1px solid ${borderColor}`,
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      },
    },
    {
      field: 'client_status', 
      headerName: 'Status',
      width: 130,
      renderCell: (params) => {
        const isActive = Number(params.value) === 1 || params.value === true;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            sx={{
              backgroundColor: isActive
                ? (isDarkMode ? 'rgba(34, 197, 94, 0.22)' : 'rgba(34, 197, 94, 0.16)')
                : (isDarkMode ? 'rgba(239, 68, 68, 0.22)' : 'rgba(239, 68, 68, 0.12)'),
              color: isActive
                ? (isDarkMode ? '#86efac' : '#15803d')
                : (isDarkMode ? '#fca5a5' : '#b91c1c'),
              fontSize: '0.75rem',
              fontWeight: 500,
              height: '24px',
              borderRadius: '4px',
              '& .MuiChip-label': {
                padding: '0 8px',
              }
            }}
          />
        );
      }
    },
    ...((isOwner(user) || isGeneralManager(user)) ? [{
      field: 'admins',
      headerName: 'Admins',
      width: 220,
      sortable: false,
      renderCell: (params) => {
        const admins = params.value || [];
        return (
          <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', alignItems: 'center', height: '100%', overflow: 'hidden' }}>
            {admins.map((admin) => (
              <Chip
                key={admin.id}
                label={`${admin.first_name} ${admin.last_name}`}
                size="small"
                sx={{
                  fontSize: '0.7rem',
                  height: '20px',
                  borderRadius: '4px',
                  backgroundColor: isDarkMode ? '#334155' : '#f1f5f9',
                  color: isDarkMode ? '#e2e8f0' : '#334155',
                  '& .MuiChip-label': { padding: '0 6px' },
                }}
              />
            ))}
          </Box>
        );
      },
    }] : []),
    {field: 'phone', headerName: 'Phone',width: 130,},
    {field: 'city', headerName: 'City',width: 100,},
    {field: 'state', headerName: 'State',width: 100,},
    {field: 'zip_code', headerName: 'Zip Code',width: 110,},

    // {field: 'total_revenue', headerName: 'Total Revenue',width: 130,},
    // {
    //   field: 'total_profit', 
    //   headerName: 'Total Profit',
    //   width: 130,
    //   align: 'center',
    //   headerAlign: 'center',
    //   renderCell: (params) => {
    //     const value = params.value;
    //     const numValue = Number(value) || 0;
    //     const formattedValue = `$${numValue.toLocaleString()}`;
    //     const isPositive = numValue > 0;
    //     return (
    //       <Typography sx={{ 
    //         color: isPositive ? '#22c55e' : 'text.primary', 
    //         fontWeight: 500,
    //         display: 'flex',
    //         alignItems: 'center',
    //         justifyContent: 'center',
    //         height: '100%',
    //         width: '100%'
    //       }}>
    //         {formattedValue}
    //       </Typography>
    //     );
    //   }
    // },
    {field: 'display_as', headerName: 'Display As',width: 130,},
    {
      field: 'invoice_pattern',
      headerName: 'Invoice Pattern',
      width: 140,
      renderCell: (params) => (
        <Typography
          component="span"
          sx={{
            fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
            fontSize: '0.8125rem',
            letterSpacing: '0.04em',
            color: 'text.primary',
          }}
        >
          {params.value || ''}
        </Typography>
      ),
    },
    {field: 'client_address', headerName: 'Client Address',width: 240,},
    {field: 'created_at', headerName: 'Created At',width: 130,valueFormatter: value => value ? value.slice(0, 10) : '',},
    {field: 'updated_at', headerName: 'Updated At',width: 130,valueFormatter: value => value ? value.slice(0, 10) : '',},
  ], [isDarkMode, !!selectedClient, user]);

  const columnFields = React.useMemo(() => columns.map((column) => column.field), [columns]);

  const activeGridPreference = React.useMemo(
    () => sanitizeGridPreference(gridPreferences ?? DEFAULT_GRID_PREFERENCE, columnFields),
    [gridPreferences, columnFields],
  );

  const columnsByField = React.useMemo(
    () => new Map(columns.map((column) => [column.field, column])),
    [columns],
  );

  const orderedColumns = React.useMemo(
    () => applyColumnOrder(columns, activeGridPreference.columnOrder),
    [columns, activeGridPreference.columnOrder],
  );

  const displayColumns = React.useMemo(
    () => (isMobile ? scaleColumnsForMobile(orderedColumns) : orderedColumns),
    [isMobile, orderedColumns],
  );

  const columnsPanelOpen = Boolean(columnsPanelAnchorEl);

  React.useEffect(() => {
    window.localStorage.setItem(GRID_PREFERENCES_STORAGE_KEY, JSON.stringify(activeGridPreference));
  }, [activeGridPreference]);

  const updateGridPreference = React.useCallback((updater) => {
    setGridPreferences((prev) => {
      const currentPreference = sanitizeGridPreference(prev ?? DEFAULT_GRID_PREFERENCE, columnFields);
      const nextPreference = typeof updater === 'function' ? updater(currentPreference) : updater;
      return sanitizeGridPreference(nextPreference, columnFields);
    });
  }, [columnFields]);

  const handleColumnsPanelToggle = (event) => {
    setColumnsPanelAnchorEl((prev) => (prev ? null : event.currentTarget));
  };

  const handleColumnsPanelClose = () => {
    setColumnsPanelAnchorEl(null);
  };

  const handleColumnVisibilityModelChange = React.useCallback((model) => {
    updateGridPreference((currentPreference) => ({
      ...currentPreference,
      columnVisibilityModel: model,
    }));
  }, [updateGridPreference]);

  const handleColumnVisibilityToggle = React.useCallback((field) => {
    updateGridPreference((currentPreference) => {
      const isVisible = currentPreference.columnVisibilityModel[field] !== false;
      return {
        ...currentPreference,
        columnVisibilityModel: {
          ...currentPreference.columnVisibilityModel,
          [field]: !isVisible,
        },
      };
    });
  }, [updateGridPreference]);

  const handleReorderColumns = React.useCallback((activeField, overField) => {
    updateGridPreference((currentPreference) => {
      const nextOrder = [...currentPreference.columnOrder];
      const fromIndex = nextOrder.indexOf(activeField);
      const toIndex = nextOrder.indexOf(overField);
      if (fromIndex === -1 || toIndex === -1 || fromIndex === toIndex) return currentPreference;

      const [movedField] = nextOrder.splice(fromIndex, 1);
      nextOrder.splice(toIndex, 0, movedField);
      return {
        ...currentPreference,
        columnOrder: nextOrder,
      };
    });
  }, [updateGridPreference]);

  const handleResetColumns = React.useCallback(() => {
    updateGridPreference({
      columnOrder: columnFields,
      columnVisibilityModel: {},
    });
  }, [updateGridPreference, columnFields]);

  // Display client details drawer
  const handleRowSelectionModelChange = (newRowSelectionModel) => {
    if (newRowSelectionModel.ids.size > 0) {
      const clientId = newRowSelectionModel.ids.values().next().value; // Get first selected row ID
      const client = clients.clients.find(c => c.id === clientId);
      if (client) setSelectedClient(client);
    }
  };

  // Open ClientDetailsDialog from email deep links (?openClientId=) or navigation state
  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    const openClientId = location?.state?.openClientId || params.get('openClientId');
    if (!openClientId) return;
    if (!clients?.clients?.length) return;

    const client = clients.clients.find((c) => c.id === openClientId);
    if (client) setSelectedClient(client);

    const next = new URLSearchParams(location.search);
    next.delete('openClientId');
    const search = next.toString();
    navigate(
      { pathname: location.pathname, search: search ? `?${search}` : '' },
      { replace: true, state: null },
    );
  }, [location?.state, location.search, clients.clients, location.pathname, navigate]);

  // Filter clients based on search query
  const filteredClients = React.useMemo(() => {
    if (!searchQuery) return clients.clients;
    const query = searchQuery.toLowerCase();
    return clients.clients.filter(client => 
      client.client_name?.toLowerCase().includes(query) ||
      client.email?.toLowerCase().includes(query) ||
      client.phone?.toLowerCase().includes(query) ||
      client.city?.toLowerCase().includes(query) ||
      client.state?.toLowerCase().includes(query) ||
      client.invoice_pattern?.toLowerCase().includes(query)
    );
  }, [clients.clients, searchQuery]);

  // Error message
  if (clients.error) {
    return (
      <Box sx={{
        color: 'text.primary',
        bgcolor: 'background.paper',
        padding: '24px',
        borderRadius: '8px',
        margin: '40px auto',
        maxWidth: '400px',
        textAlign: 'center',
        fontWeight: 'bold',
        fontSize: '1.2rem',
        boxShadow: isDarkMode ? '0 2px 8px rgba(211,47,47,0.3)' : '0 2px 8px rgba(211,47,47,0.15)'
      }}>
        <span style={{ fontSize: '2rem', display: 'block', marginBottom: '12px' }}>⚠️</span>
        {clients.error}
      </Box>
    );
  }

  return (
    <>
      <Paper 
        elevation={0}
        sx={{ 
          height: '100vh', 
          width: '100%', 
          padding: { xs: 1, md: 3 },
          backgroundColor: 'background.default',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {/* Header with title and Add Client button */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: { xs: 2, md: 3 }, gap: 1, flexWrap: 'wrap', flexShrink: 0 }}>
          <Typography 
            variant="h4" 
            component="h1"
            sx={{ 
              fontWeight: 700,
              fontSize: { xs: '1.25rem', md: '2rem' },
              color: 'text.primary',
            }}
          >
            Clients
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            {isMobile && (
              <IconButton
                onClick={handleColumnsPanelToggle}
                aria-label="Manage columns"
                size="small"
                sx={{
                  border: '1px solid',
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  borderRadius: 1,
                }}
              >
                <ViewColumnIcon fontSize="small" />
              </IconButton>
            )}
            {canAddOrEditClient(user) && (
              <Button
                variant="contained"
                onClick={() => setAddClientDialogOpen(true)}
                sx={{
                  backgroundColor: isDarkMode ? '#fff' : '#000',
                  color: isDarkMode ? '#000' : '#fff',
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 3,
                  py: 1.25,
                  borderRadius: 1.5,
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#e0e0e0' : '#333',
                    boxShadow: 'none',
                  }
                }}
              >
                + Add Client
              </Button>
            )}
          </Box>
        </Box>

        {/* Search bar */}
        <Box sx={{ mb: { xs: 2, md: 3 }, flexShrink: 0 }}>
          <TextField
            fullWidth
            placeholder="Search clients by name or email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 1.5,
                backgroundColor: 'background.paper',
                fontSize: '0.875rem',
                height: '42px',
                '& input::placeholder': {
                  color: 'text.secondary',
                  opacity: 1,
                },
                '& fieldset': {
                  borderColor: isDarkMode ? '#334155' : '#e0e0e0',
                  borderWidth: '1px',
                },
                '&:hover fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                },
                '&.Mui-focused fieldset': {
                  borderColor: isDarkMode ? '#475569' : '#e0e0e0',
                  borderWidth: '1px',
                },
              }
            }}
          />
        </Box>

        {/* Data Grid */}
        <Box sx={{ 
          backgroundColor: 'background.paper', 
          borderRadius: 2,
          border: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
          overflow: 'hidden',
          position: 'relative',
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
          // Reserve right gutter so the columns panel does not cover grid/footer content.
          paddingRight: isMobile ? 0 : '48px',
        }}>
          {/* Columns Panel Button (desktop rail) */}
          {!isMobile && (
            <Box
              sx={{
                position: 'absolute',
                right: 0,
                top: 0,
                bottom: 0,
                width: '48px',
                zIndex: 10,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'flex-start',
                backgroundColor: 'background.paper',
                borderLeft: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              }}
            >
              <Button
                onClick={handleColumnsPanelToggle}
                sx={{
                  minWidth: '48px',
                  width: '48px',
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'flex-start',
                  padding: '12px 0',
                  gap: 1,
                  backgroundColor: 'transparent',
                  color: 'text.primary',
                  borderRadius: 0,
                  '&:hover': {
                    backgroundColor: isDarkMode ? '#1e293b' : '#f5f5f5',
                  },
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    bottom: 0,
                    width: '3px',
                    backgroundColor: isDarkMode ? '#fff' : '#000',
                  },
                }}
                aria-label="Manage columns"
              >
                <ViewColumnIcon sx={{ fontSize: '20px', mb: 0.5, color: 'text.primary' }} />
                <Typography
                  sx={{
                    writingMode: 'vertical-rl',
                    textOrientation: 'mixed',
                    fontSize: '0.75rem',
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                    color: 'text.primary',
                  }}
                >
                  Columns
                </Typography>
              </Button>
            </Box>
          )}

          <ClientsColumnsPopover
            open={columnsPanelOpen}
            anchorEl={columnsPanelAnchorEl}
            onClose={handleColumnsPanelClose}
            isDarkMode={isDarkMode}
            activeGridPreference={activeGridPreference}
            columnsByField={columnsByField}
            onToggleColumnVisibility={handleColumnVisibilityToggle}
            onReorderColumns={handleReorderColumns}
            onResetColumns={handleResetColumns}
          />

          <DataGrid
            rows={filteredClients}
            loading={clients.loading}
            columns={displayColumns}
            density={isMobile ? 'compact' : 'standard'}
            columnVisibilityModel={activeGridPreference.columnVisibilityModel}
            onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
            initialState={{ pagination: { paginationModel } }}
            pageSizeOptions={[5, 10, 20, 30, 50, 100]}
            onRowSelectionModelChange={handleRowSelectionModelChange}
            sx={{ 
              border: 0,
              height: '100%',
              '& .MuiDataGrid-columnHeaders': {
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
              },
              '& .MuiDataGrid-columnHeaderTitle': { 
                fontWeight: 600,
                fontSize: '0.8125rem',
                color: 'text.secondary',
              },
              '& .MuiDataGrid-cell': { 
                fontSize: '0.875rem',
                color: 'text.primary',
                borderBottom: isDarkMode ? '1px solid #334155' : '1px solid #f5f5f5',
              },
              '& .MuiDataGrid-row': {
                '&:hover': {
                  backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                },
              },
              '& .MuiDataGrid-footerContainer': { 
                borderTop: isDarkMode ? '1px solid #334155' : '1px solid #e0e0e0',
                backgroundColor: isDarkMode ? '#1e293b' : '#fafafa',
                position: 'relative',
                zIndex: 2,
              },
            }}
          />
        </Box>
      </Paper>

      {/* Client details drawer */}
      {ClientDetailsDialog({ selectedClient, setSelectedClient, user })}
      {/* Add client drawer */}
      {canAddOrEditClient(user) && AddClientDialog({ setAddClientDialogOpen, addClientDialogOpen, user })}
    </>
  );
}
